import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BillingStatusService } from '../services/billing-status.service';
import { isBillingForbidden } from '../utils/billing-restriction.utils';

/**
 * v1.6.1538 — catches the billing 403 from ANY staff route
 * (Asana 1218431462710845).
 *
 * The block can arrive mid-session with no warning phase — a manual block, or a
 * paused subscription — so it has to be handled from any screen rather than
 * only at load.
 *
 * THE ERROR IS RE-THROWN, never swallowed. The calling screen keeps its own
 * handling and, crucially, its unsaved form data: the spec asks for a
 * mid-session block not to cost an in-flight edit, and a swallowed error would
 * leave a save spinner running forever with the form already discarded.
 */
@Injectable()
export class BillingInterceptor implements HttpInterceptor {
  /**
   * Injector rather than the service directly: BillingStatusService depends on
   * HttpClient, and injecting it into an interceptor at construction time is a
   * circular dependency.
   */
  constructor(private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        /**
         * A billing 403 and a permission 403 are the same status code. They are
         * told apart ONLY by `data.reason` — treat a permission denial as a
         * billing block and every ordinary 403 becomes a full-screen lock-out.
         */
        if (isBillingForbidden(err)) {
          // The always-reachable routes should never produce this, but if one
          // ever did, adopting it would lock the panel out of its own recovery.
          const gated = !BillingStatusService.NEVER_GATED.some(p => req.url.includes(p));
          if (gated) {
            try { this.injector.get(BillingStatusService).adoptFromForbidden(err); }
            catch { /* never let the notice break the request pipeline */ }
          }
        }
        return throwError(() => err);
      }),
    );
  }
}
